@if (loading) {
  <div class="d-flex justify-content-center">
    <ix-spinner size="large" class="mt-5"></ix-spinner>
  </div>
} @else {
  <div class="brand-domains">
    <div class="d-flex justify-content-between align-items-center mt-4">
      <div>
        <ix-typography format="h4" id="brandDomainsHeader">
          {{ 'organizations.brand_domains.header' | translate }}
        </ix-typography>
        <ix-typography format="body-sm" textColor="soft">
          {{ 'organizations.brand_domains.subheader' | translate }}
        </ix-typography>
      </div>
      <ix-button
        id="brandDomainsAddButton"
        icon="plus"
        variant="primary"
        (click)="openAdd()"
        onkeypress=""
      >
        {{ 'organizations.brand_domains.add' | translate }}
      </ix-button>
    </div>

    @if (domains.length === 0) {
      <ix-empty-state
        class="mt-5"
        id="brandDomainsEmptyState"
        icon="globe"
        header="{{ 'organizations.brand_domains.empty.header' | translate }}"
        subHeader="{{ 'organizations.brand_domains.empty.subheader' | translate }}"
        action="{{ 'organizations.brand_domains.add' | translate }}"
        (actionClick)="openAdd()"
      ></ix-empty-state>
    } @else {
      <table class="brand-domains__table mt-4" id="brandDomainsTable">
        <caption class="visually-hidden">
          {{ 'organizations.brand_domains.header' | translate }}
        </caption>
        <thead>
          <tr>
            <th scope="col">{{ 'organizations.brand_domains.col_root_domain' | translate }}</th>
            <th scope="col">{{ 'organizations.brand_domains.col_brand_host' | translate }}</th>
            <th scope="col">{{ 'organizations.brand_domains.col_logo' | translate }}</th>
            <th scope="col">{{ 'organizations.brand_domains.col_default' | translate }}</th>
            <th scope="col" class="text-end">
              {{ 'organizations.brand_domains.col_actions' | translate }}
            </th>
          </tr>
        </thead>
        <tbody>
          @for (row of rows; track row.domain.id) {
            <tr [attr.data-domain-id]="row.domain.id">
              <td>
                <div class="brand-domains__root">{{ row.domain.rootDomain }}</div>
                @if (row.domain.label) {
                  <ix-typography format="caption" textColor="soft">
                    {{ row.domain.label }}
                  </ix-typography>
                }
              </td>
              <td><code>{{ row.domain.host }}</code></td>
              <td>
                @if (row.logoPreview; as preview) {
                  <img
                    class="brand-domains__logo"
                    [src]="preview"
                    [alt]="row.domain.host"
                    width="128"
                    height="32"
                  />
                } @else {
                  <ix-pill [outline]="true" variant="neutral">
                    {{ 'organizations.brand_domains.logo_none' | translate }}
                  </ix-pill>
                }
              </td>
              <td>
                @if (row.domain.isDefault) {
                  <ix-pill variant="primary" icon="star">
                    {{ 'organizations.brand_domains.default' | translate }}
                  </ix-pill>
                }
              </td>
              <td class="text-end">
                <ix-icon-button
                  icon="pen"
                  ghost
                  [attr.aria-label]="'organizations.brand_domains.edit' | translate"
                  (click)="openEdit(row.domain)"
                  onkeypress=""
                ></ix-icon-button>
                <ix-icon-button
                  icon="trashcan"
                  ghost
                  [disabled]="row.domain.isDefault"
                  [attr.aria-label]="'organizations.brand_domains.delete' | translate"
                  (click)="confirmDelete(row.domain)"
                  onkeypress=""
                ></ix-icon-button>
              </td>
            </tr>
          }
        </tbody>
      </table>
    }
  </div>
}

<ix-drawer
  [show]="drawerOpen"
  (drawerClose)="closeDrawer()"
  width="32"
  [closeOnClickOutside]="false"
  id="brandDomainDrawer"
>
  <div class="brand-domains__drawer">
    <ix-typography format="h4">
      {{
        (editing
          ? 'organizations.brand_domains.drawer_edit_header'
          : 'organizations.brand_domains.drawer_add_header'
        ) | translate
      }}
    </ix-typography>

    <form [formGroup]="form" novalidate class="needs-validation mt-3">
      <ix-input
        id="brandDomainHost"
        class="w-100"
        formControlName="host"
        [label]="'organizations.brand_domains.field_root_domain' | translate"
        [helperText]="'organizations.brand_domains.field_root_domain_hint' | translate"
        [invalidText]="'organizations.brand_domains.field_root_domain_error' | translate"
        [required]="true"
        placeholder="example.com"
      />

      <ix-input
        id="brandDomainDerivedHost"
        class="w-100 mt-3"
        [label]="'organizations.brand_domains.field_brand_host' | translate"
        [helperText]="'organizations.brand_domains.field_brand_host_hint' | translate"
        [value]="derivedHost"
        [readonly]="true"
      />

      <ix-input
        id="brandDomainLabel"
        class="w-100 mt-3"
        formControlName="label"
        [label]="'organizations.brand_domains.field_label' | translate"
      />

      <ix-typography format="label" class="d-block mt-4">
        {{ 'organizations.brand_domains.field_logo' | translate }}
      </ix-typography>
      <ix-radio-group
        class="mt-1"
        [value]="form.controls.logoMode.value"
        (valueChange)="onLogoModeChange($any($event.detail))"
      >
        <ix-radio
          value="UPLOAD"
          id="brandDomainLogoUpload"
          [label]="'organizations.brand_domains.logo_mode_upload' | translate"
        ></ix-radio>
        <ix-radio
          value="NONE"
          id="brandDomainLogoNone"
          [label]="'organizations.brand_domains.logo_mode_none' | translate"
        ></ix-radio>
        <ix-radio
          value="REFERENCE"
          id="brandDomainLogoReference"
          [disabled]="referenceableDomains.length === 0"
          [label]="'organizations.brand_domains.logo_mode_reference' | translate"
        ></ix-radio>
      </ix-radio-group>

      @if (form.controls.logoMode.value === 'UPLOAD') {
        <ix-upload
          id="brandDomainLogoFile"
          class="mt-2"
          accept="image/png,image/svg+xml"
          [multiple]="false"
          [selectFileText]="'organizations.brand_domains.logo_upload_text' | translate"
          [i18nUploadFile]="'organizations.brand_domains.logo_upload_placeholder' | translate"
          (filesChanged)="onLogoFilesChanged($event)"
        ></ix-upload>
        <ix-typography format="caption" textColor="soft" class="d-block mt-1">
          {{ 'organizations.brand_domains.logo_requirements' | translate }}
        </ix-typography>
        @if (logoError) {
          <ix-typography format="caption" textColor="alarm" class="d-block mt-1">
            {{ logoError | translate }}
          </ix-typography>
        }
        @if (form.controls.logoUrl.value) {
          <div class="brand-domains__preview mt-2">
            <img
              [src]="form.controls.logoUrl.value"
              [alt]="logoFileName ?? 'logo'"
              width="192"
              height="64"
            />
            @if (logoFileName) {
              <ix-typography format="caption" textColor="soft">
                {{ logoFileName }}
              </ix-typography>
            }
          </div>
        }
      }

      @if (form.controls.logoMode.value === 'REFERENCE') {
        <ix-select
          id="brandDomainLogoRef"
          class="w-100 mt-2"
          [value]="form.controls.refDomainId.value"
          (valueChange)="form.controls.refDomainId.setValue($any($event.detail))"
        >
          @for (candidate of referenceableDomains; track candidate.id) {
            <ix-select-item
              [label]="candidate.host"
              [value]="candidate.id"
            ></ix-select-item>
          }
        </ix-select>
      }

      <div class="d-flex align-items-center justify-content-between mt-4">
        <ix-typography format="label">
          {{ 'organizations.brand_domains.field_default' | translate }}
        </ix-typography>
        <ix-toggle
          id="brandDomainDefaultToggle"
          [checked]="form.controls.isDefault.value"
          [disabled]="editing?.isDefault === true"
          (checkedChange)="form.controls.isDefault.setValue($any($event.detail))"
        ></ix-toggle>
      </div>
      <ix-typography format="caption" textColor="soft" class="d-block mt-1">
        {{ 'organizations.brand_domains.field_default_hint' | translate }}
      </ix-typography>

      @if (errorMessages.length > 0) {
        <ix-message-bar type="alarm" class="mt-3" [persistent]="true">
          @for (message of errorMessages; track message) {
            <div>{{ message }}</div>
          }
        </ix-message-bar>
      }
    </form>

    <ix-modal-footer class="mt-4">
      <ix-button
        id="brandDomainCancelButton"
        variant="secondary"
        (click)="closeDrawer()"
        onkeypress=""
      >
        {{ 'general.cancel' | translate }}
      </ix-button>
      <ix-button
        id="brandDomainSaveButton"
        variant="primary"
        [disabled]="!canSave"
        [loading]="saving"
        (click)="save()"
        onkeypress=""
      >
        {{ 'general.save' | translate }}
      </ix-button>
    </ix-modal-footer>
  </div>
</ix-drawer>

<ng-template #deleteModal let-modal>
  <ix-modal>
    <ix-modal-header id="brandDomainDeleteHeader">
      {{ 'organizations.brand_domains.delete_header' | translate }}
    </ix-modal-header>
    <ix-modal-content id="brandDomainDeleteContent">
      {{
        'organizations.brand_domains.delete_content'
          | translate: {host: modal.data?.host}
      }}
    </ix-modal-content>
    <ix-modal-footer>
      <ix-button variant="secondary" (click)="modal.dismiss()" onkeypress="">
        {{ 'general.cancel' | translate }}
      </ix-button>
      <ix-button variant="danger" (click)="modal.close(true)" onkeypress="">
        {{ 'organizations.brand_domains.delete' | translate }}
      </ix-button>
    </ix-modal-footer>
  </ix-modal>
</ng-template>
